/* ============================================================================
   DEFINE YOUR UNIFORM — Collections - Carousel 3333:173626 (desktop; the
   mobile layout is this build's, after Asket's Capsule Wardrobe)

   What's being decided:
     • One colour for the whole uniform. The swatch row (the eight core
       colours) re-dresses the table at once, left to right — scrubs in the
       colour itself, underscrubs and accessories in its complements. Each table
       position is a slot with candidates in order of preference: the slot
       shows the first one with a real ghost in the picked colour, else the
       slot simply empties. (Only the old flat export, without dyu2, falls
       back to recolouring a product's own render with recolor.js.) The grid is allowed to change product with
       the colour; nothing is ever shown "not available", and no on-model
       photo ever stands in for a ghost.
     • Availability as a fact: 01 CORE reads 24/7 (Figma); 02 LAYERING
       SYSTEM's percentage is how many of its first-choice garments are made
       in the picked colour.
     • Desktop: the table full width, strokes shared (one 1px line between
       every bordered label, never two). Hover (Figma 3542:473553) outlines
       the card, dims its ghost to 50%, and reveals "quickbuy +" the way the
       Uniform Builder's cards do.
     • Mobile (the ledger): no tabs and no availability — each layer a band
       of square ghost tiles six to a row, a vertical label in the gutter,
       one hairline grid through every cell, titles hidden, the PLP's square
       Quick Buy on each, and a colour change crossfades every tile at once.
     • QUICK BUY is the PDP drawer (drawers.js initQuickBuy) in the uniform's
       colour; ADD TO BAG lands in the lab's shared bag.
   ========================================================================== */

import { addToFigsBag, openFigsBag } from '/src/figs-nav/figs-nav.js';
import { animate, hover, track, MO, state, play, tween, $, $$, mqDesktop } from '../10-pdp-prototype/mo.js';
import { swapText, announce, tm } from '../10-pdp-prototype/ui.js';
import { initQuickBuy } from '../10-pdp-prototype/drawers.js';
import { SIZE_NAMES } from '../10-pdp-prototype/data.js';
import { recolor } from '../15-uniform-builder/recolor.js';
import { DATA, SYSTEMS, ROWS, PAIRINGS, systemOfCol, ownBase } from './data.js';
import { gender, onGender } from './gender.js';

const root = $('[data-dyu]');
const table = $('[data-dyu-table]', root);
const swatchRow = $('[data-dyu-swatches]', root);
const colorLabel = $('[data-dyu-color-name]', root);
const ledger = $('[data-dyu-ledger]', root);
const QB_D = 'assets/ui/quick-buy-28.svg';
const QB_D_HOVER = 'assets/ui/quick-buy-28-hover.svg';
const QB_M = 'assets/ui/quick-buy-24.svg';
const LIGHT = new Set(['white', 'off-white', 'optic-white']);
const money = (n) => `$${Number(n) % 1 ? Number(n).toFixed(2) : Number(n)}`;

const ui = { color: { women: 'navy', men: 'navy' } };
const slots = () => DATA[gender()].dyuSlots;
const swatches = () => DATA[gender()].swatches;
const swatch = (slug = ui.color[gender()]) => swatches().find((s) => s.slug === slug) || swatches()[0];
const nameOf = (p) => p.name || p.title || '';

/* ---- which product a slot shows in a colour, and its ghost --------------
   Scrubs match the uniform colour. Underscrubs and accessories are worn
   WITH it, so they take the colour's complements instead, best first
   (data.js PAIRINGS — e.g. black underscrubs under navy, charcoal under
   mauve, white with royal). The slot keeps its product preference: the
   first candidate shown in any of the complements wins. */
const colorsFor = (row, slug) => PAIRINGS[gender()]?.[slug]?.[row] || [slug];
const colorName = (p, slug) => p?.colors?.[slug]?.siteName || swatches().find((s) => s.slug === slug)?.name || slug;
function pick(slot, sw) {
  const colors = colorsFor(slot.row, sw.slug);
  for (const p of slot.candidates) {
    for (const c of colors) {
      const pc = p.colors?.[c];
      if (pc?.offered !== false && pc?.ghost) return { p, src: pc.ghost, tinted: false, color: c };
    }
  }
  if (DATA[gender()].dyuStrict) return null;
  const rec = slot.candidates.find((p) => p.colors?.[sw.slug]?.offered && ownBase(p));
  if (rec) return { p: rec, base: ownBase(rec), tinted: true, color: sw.slug };
  return null;
}
async function srcOf(choice, sw) {
  if (!choice) return null;
  if (!choice.tinted) return choice.src;
  return recolor(choice.base, { slug: sw.slug, hex: sw.tint || sw.hex }, { source: 'navy' });
}

/* load an image off-screen; resolve with the URL once it can paint, or null */
const loaded = new Map();
function preload(src) {
  if (!src) return Promise.resolve(null);
  if (!loaded.has(src)) {
    loaded.set(src, new Promise((resolve) => {
      const img = new Image();
      img.decoding = 'async';
      const done = (ok) => resolve(ok ? src : null);
      img.onload = () => (img.decode ? img.decode().then(() => done(true), () => done(true)) : done(true));
      img.onerror = () => done(false);
      img.src = src;
    }).then((r) => { if (!r) loaded.delete(src); return r; }));
  }
  return loaded.get(src);
}

/* ============================================================
   RENDER
   ============================================================ */
function renderSwatches() {
  const cur = swatch();
  ui.color[gender()] = cur.slug;
  swatchRow.innerHTML = swatches().map((s) => `
    <button class="ncl-sw${LIGHT.has(s.slug) ? ' ncl-sw--light' : ''}" type="button" role="radio" aria-checked="${s.slug === cur.slug}"
      tabindex="${s.slug === cur.slug ? 0 : -1}" aria-label="${s.name}" data-sw="${s.slug}" style="--sw:${s.hex}">
      <span class="ncl-sw__chip"></span><span class="ncl-sw__rule"></span>
    </button>`).join('');
  colorLabel.textContent = cur.name;
}

function availability(sys, slug) {
  const own = slots().filter((s) => systemOfCol(s.col)?.id === sys.id);
  if (!sys.avail || !own.length) return null;
  if (sys.avail === 'always') return { text: '24/7', low: false };
  /* a first choice counts when it's made in the colour (or, for underscrubs
     and accessories, in one of its complements) */
  const made = own.filter((s) => colorsFor(s.row, slug).some((c) => s.candidates[0].colors?.[c]?.offered)).length;
  const pct = Math.round((made / own.length) * 100);
  return { text: `${pct}%`, low: pct < 100 };
}
const availHTML = (a) => (a ? `<span class="ncl-avail">availability: <span class="ncl-avail__v"${a.low ? ' data-low' : ''}>${a.text}</span></span>` : '');

function cardHTML(p) {
  const name = nameOf(p);
  return `
    <article class="ncl-card" data-card="${p.id}">
      <span class="ncl-card__plate" data-plate>
        <span class="ncl-card__ghosts" data-ghosts></span>
        ${p.isNew ? '<span class="ncl-card__badge">New</span>' : ''}
        <span class="ncl-card__hover" aria-hidden="true" data-card-hover><span class="ncl-card__hover-label">quickbuy</span><img src="assets/ui/icon-plus-16.svg" alt="" width="16" height="16"></span>
      </span>
      <p class="ncl-card__name">${tm(name)}</p>
      <button class="ncl-card__hit" type="button" aria-haspopup="dialog" data-qb-open="${p.id}"></button>
      <span class="ncl-card__qb" aria-hidden="true">
        <img class="ncl-card__qb-icon ncl-card__qb-icon--m" src="${QB_M}" alt="" width="24" height="24">
        <img class="ncl-card__qb-icon ncl-card__qb-icon--d" src="${QB_D}" alt="" width="28" height="28">
        <img class="ncl-card__qb-icon ncl-card__qb-icon--d-hover" src="${QB_D_HOVER}" alt="" width="28" height="28">
      </span>
    </article>`;
}

function render() {
  renderSwatches();
  const slug = swatch().slug;
  const hbar = `
    <div class="ncl-dyu__hbar">
      <span class="ncl-hcell ncl-hcell--corner" aria-hidden="true"></span>
      ${SYSTEMS.map((s) => `<p class="ncl-hcell" style="--col:${s.cols[0] + 1};--span:${s.cols[1] - s.cols[0] + 1}">
        <span>${s.label}</span><span class="ncl-hcell__avail" data-hbar-avail="${s.id}">${availHTML(availability(s, slug))}</span></p>`).join('')}
    </div>`;
  const rows = ROWS.map((r, ri) => {
    const cells = [];
    for (let col = 1; col <= 11; col += 1) {
      const slot = slots().find((x) => x.row === r.id && x.col === col);
      cells.push(`<div class="ncl-cell${slot ? '' : ' ncl-cell--none'}" style="--col:${col + 1}" data-col="${col}" data-row="${r.id}" data-sys="${systemOfCol(col).id}"${slot ? ' data-slot' : ''}></div>`);
    }
    return `
      <div class="ncl-row" data-row="${r.id}" style="--ri:${ri}">
        <p class="ncl-row__label"><span>${r.label}</span></p>
        <div class="ncl-row__cells">${cells.join('')}</div>
      </div>`;
  }).join('');
  table.innerHTML = `${hbar}${rows}`;
  ledger.innerHTML = ledgerHTML();
  bindCardHover();
  paint({ wave: false });
}

/* Mobile — Asket's Capsule Wardrobe, read like the desktop table: each
   system (01 CORE, 02 LAYERING SYSTEM, 03 OFF SHIFT) is a section under a
   horizontal label bar, and inside it each layer is a band — its name in a
   vertical gutter — of square tiles six to a row. One hairline grid runs
   through every cell, filled or not. The shape is fixed per gender: every
   slot keeps its cell in every colour (a garment not made in the colour
   leaves its cell blank, as in the table) and blank cells pad each band's
   last row once, at render — so a colour change never moves the page.
   Same slots as the table, so the same paint fills both. */
const PER_ROW = 6;
const XFADE = { duration: 0.5, ease: [0.35, 0, 0.15, 1] };
function ledgerHTML() {
  return SYSTEMS.map((sys) => {
    const bands = ROWS.map((r) => {
      const here = slots().filter((x) => x.row === r.id && systemOfCol(x.col)?.id === sys.id).sort((a, b) => a.col - b.col);
      if (!here.length) return '';
      return `
        <div class="ncl-ledger__band" data-band="${r.id}">
          <p class="ncl-ledger__label"><span>${r.label.replace(/^[^.]+\.\s*/, '')}</span></p>
          <div class="ncl-ledger__cells">
            ${here.map((x) => `<div class="ncl-cell ncl-cell--tile" data-col="${x.col}" data-row="${r.id}" data-sys="${sys.id}" data-slot></div>`).join('')}
            ${'<div class="ncl-cell ncl-cell--blank" aria-hidden="true"></div>'.repeat(Math.ceil(here.length / PER_ROW) * PER_ROW - here.length)}
          </div>
        </div>`;
    }).join('');
    if (!bands) return '';
    return `
      <section class="ncl-ledger__sys" data-ledger-sys="${sys.id}" aria-label="${sys.label.replace(/^\d+\s*/, '')}">
        <p class="ncl-ledger__sys-label" aria-hidden="true">${sys.label}</p>
        ${bands}
      </section>`;
  }).join('');
}

/* ============================================================
   PAINT — every slot in the picked colour. A slot that changes product
   swaps its card; one that keeps its product swaps only the ghost, and
   only once the new one has decoded (an image is never left blank or
   broken mid-swap). Cells are emptied, never disabled.
   ============================================================ */
let paintToken = 0;
async function paint({ wave = true } = {}) {
  const token = ++paintToken;
  const sw = swatch();
  SYSTEMS.forEach((s) => {
    const h = $(`[data-hbar-avail="${s.id}"]`, table);
    if (h) h.innerHTML = availHTML(availability(s, sw.slug));
  });
  const cells = [...$$('[data-slot]', table), ...$$('[data-slot]', ledger)];
  /* 1 · choose every slot's garment and load every ghost first, so the grid
     changes in one frame — no tile updating on its own as its image lands */
  const plans = await Promise.all(cells.map(async (cell) => {
    const slot = slots().find((x) => x.row === cell.dataset.row && x.col === Number(cell.dataset.col));
    const choice = pick(slot, sw);
    return { cell, choice, src: await preload(await srcOf(choice, sw)) };
  }));
  if (token !== paintToken) return;
  /* 2 · then every cell at once */
  const animated = wave && !state.reduced;
  plans.forEach((plan) => applyCell(plan, sw, animated, token));
}

/* one cell: the desktop table waves left to right and swaps a card out then
   in (cards are in flow there); the mobile ledger crossfades every tile at
   once (Asket) — its tiles hold their grey, so only the garments change */
function applyCell({ cell, choice, src }, sw, animated, token) {
  const tile = cell.classList.contains('ncl-cell--tile');
  const delay = animated && !tile
    ? (Number(cell.dataset.col) - 1) * 0.03 + ROWS.findIndex((r) => r.id === cell.dataset.row) * 0.05 : 0;
  const current = $('[data-card]:not([data-leaving])', cell);
  const leave = (card) => {
    card.setAttribute('data-leaving', '');
    if (!animated) { card.remove(); return null; }
    const a = tile
      ? animate(card, { opacity: 0 }, XFADE)
      : animate(card, { opacity: [1, 0], scale: [1, 0.96] }, { duration: MO.quick, delay, ease: MO.eExit });
    return a.finished.then(() => card.remove(), () => card.remove());
  };
  /* empty: the product isn't made in this colour (or nothing has a ghost) */
  if (!choice || !src) {
    cell.toggleAttribute('data-empty', true);
    if (current) leave(current);
    return;
  }
  cell.removeAttribute('data-empty');
  const label = `Quick Buy: ${nameOf(choice.p).replace(/"/g, '')}, ${colorName(choice.p, choice.color)}`;
  const enter = () => {
    cell.insertAdjacentHTML('beforeend', cardHTML(choice.p));
    const card = $$('[data-card]', cell).pop();
    setGhost(card, src, false);
    $('.ncl-card__hit', card).setAttribute('aria-label', label);
    card.dataset.tinted = String(choice.tinted);
    card.dataset.color = choice.color;
    return card;
  };
  if (current && current.dataset.card === choice.p.id) {
    /* same garment, new colour: the ghost crossfades in place */
    setGhost(current, src, animated, delay, tile ? XFADE : null);
    $('.ncl-card__hit', current).setAttribute('aria-label', label);
    current.dataset.tinted = String(choice.tinted);
    current.dataset.color = choice.color;
    return;
  }
  if (tile) {
    /* the ledger: the new garment fades in over the old as the old fades out */
    const card = enter();
    if (current) leave(current);
    if (animated) animate(card, { opacity: [0, 1] }, XFADE);
    return;
  }
  /* the table: out, then in, so the cell never holds two cards */
  const out = current ? leave(current) : null;
  const show = () => {
    if (token !== paintToken) return;
    const card = enter();
    if (animated) animate(card, { opacity: [0, 1], scale: [0.96, 1] }, { duration: MO.base, delay: current ? 0 : delay, ease: MO.eEntrance });
  };
  if (out) out.then(show); else show();
}

/* the ghost swap: the new image goes on top and fades in while the old one
   fades out, then the old one leaves */
function setGhost(card, src, animated, delay = 0, fade = null) {
  const plate = $('[data-ghosts]', card);
  const old = $('img.ncl-card__ghost:not([data-leaving])', plate);
  if (old && old.getAttribute('src') === src) return;
  const img = document.createElement('img');
  img.className = 'ncl-card__ghost';
  img.alt = '';
  img.decoding = 'async';
  img.draggable = false;
  img.src = src;
  img.addEventListener('error', () => img.remove(), { once: true });
  plate.append(img);
  if (!old) return;
  old.setAttribute('data-leaving', '');
  if (!animated) { old.remove(); return; }
  const fx = fade || { duration: MO.base, delay, ease: MO.eStandard };
  animate(img, { opacity: [0, 1] }, fx);
  animate(old, { opacity: [1, 0] }, fx).finished.then(() => old.remove(), () => old.remove());
}

/* ============================================================
   HOVER — Table Uniform Card, hover (3542:473553), with the Uniform
   Builder card's reveal (15 initCardHover): the card takes a 1px
   Border/Default, its ghost eases to 50%, and "quickbuy +" rises in over
   it. Nothing grows or moves; keyboard focus shows the same state (CSS).
   motion's hover() is pointer-only, so touch keeps the square Quick Buy.
   ============================================================ */
let unhover = [];
function bindCardHover() {
  unhover.splice(0).forEach((fn) => fn?.());
  $$('[data-slot]', table).forEach((cell) => {
    let on = false;
    let ins = [];
    let parts = [];
    /* hand the rest state back to the stylesheet once nothing is running
       (a hover cut short mid-rise must not leave the link stuck) */
    const release = () => {
      if (on) return;
      if (parts.some((el) => el.getAnimations().some((x) => x.playState === 'running'))) { setTimeout(release, 60); return; }
      parts.forEach((el) => { el.style.opacity = ''; el.style.transform = ''; });
    };
    unhover.push(hover(cell, () => {
      const card = $('[data-card]:not([data-leaving])', cell);
      if (!mqDesktop.matches || !card) return undefined;
      on = true;
      const ghosts = $('[data-ghosts]', card);
      const cta = $('[data-card-hover]', card);
      parts = [ghosts, cta];
      cell.setAttribute('data-hover', '');
      ins = [
        track(animate(ghosts, { opacity: 0.5 }, { duration: MO.base, ease: MO.eStandard })),
        track(animate(cta, { opacity: 1, y: [MO.riseXs, 0] }, { duration: MO.base, ease: MO.eEntrance })),
      ];
      return () => {
        on = false;
        cell.removeAttribute('data-hover');
        ins.forEach((x) => x?.stop?.());
        const out = [
          track(animate(ghosts, { opacity: 1 }, { duration: MO.quick, ease: MO.eStandard })),
          track(animate(cta, { opacity: 0 }, { duration: MO.quick, ease: MO.eStandard })),
        ];
        Promise.all(out.map((x) => x?.finished)).then(release, release);
        setTimeout(release, (MO.quick / (state.speed || 1)) * 1000 + 60);
      };
    }));
  });
}
/* (the bindings outlive a Replay — only render() rebinds them) */
const productOf = (card) => slots().flatMap((s) => s.candidates).find((p) => p.id === card.dataset.card);

/* ============================================================
   QUICK BUY — the PDP drawer in the uniform's colour
   ============================================================ */
let qbProduct = null;
const quickBuy = initQuickBuy({
  root: $('[data-drawer="quickbuy"]'),
  onAdd: (prod, sel) => {
    const p = prod.source;
    const slug = sel.color || swatch().slug;
    const c = p.colors?.[slug] || {};
    addToFigsBag({
      id: p.handle || p.id, name: p.title || p.name, color: colorName(p, slug), size: sel.size, type: sel.type,
      price: p.price, image: c.gallery?.[0] || prod.gallery[0], fallback: c.gallery?.[1] || null, url: prod.url,
    }, { open: false }).then(() => announce(`Added ${p.title || p.name}, ${colorName(p, slug)}, ${SIZE_NAMES[sel.size] || sel.size}, to your bag.`));
  },
  onAdded: () => openFigsBag(),
});
/* Quick Buy's own gallery is on model (the bag and the drawer always are);
   only the table is ghosts */
const qbGallery = (p, slug) => {
  const c = p.colors?.[slug];
  return c?.gallery?.length ? c.gallery.slice(0, 4) : [ownBase(p)].filter(Boolean);
};
const qbUrl = (p, slug) => (p.href && !/^https?:/.test(p.href) ? `${p.href.split('?')[0]}?color=${slug}` : (p.colors?.[slug]?.url || p.url || '#'));
function openQuickBuy(p, trigger, shown) {
  const want = shown || swatch().slug;
  const colors = swatches().filter((s) => p.colors?.[s.slug]?.offered);
  const slug = p.colors?.[want]?.offered ? want : (colors[0]?.slug || want);
  qbProduct = p;
  const sizes = p.sizes?.length ? p.sizes : ['O/S'];
  quickBuy.open({
    source: p, name: p.title || p.name, price: p.price || 0, sizes, types: p.types?.length ? p.types : ['Regular'],
    typeLabel: p.typeLabel, url: qbUrl(p, slug), color: slug,
    colors: colors.length ? { core: colors.map((s) => ({ slug: s.slug, name: s.name, hex: s.hex })), limited: [] } : null,
    gallery: qbGallery(p, slug), galleryOnWhite: !p.colors?.[slug]?.gallery?.length, slideClass: 'ncl-qb__slide',
    layer: p.row, gender: gender(),
  }, trigger, { size: sizes.length === 1 ? sizes[0] : null });
}
$('[data-qb-colors]').addEventListener('click', (e) => {
  const b = e.target.closest('[data-color]');
  if (!b || !qbProduct) return;
  const slug = b.dataset.color;
  const gal = $('[data-qb-gallery]');
  const imgs = qbGallery(qbProduct, slug);
  gal.innerHTML = imgs.map((src, i) => `
    <figure class="qb__slide ncl-qb__slide${qbProduct.colors?.[slug]?.gallery?.length ? '' : ' qb__slide--white'}">
      <img src="${src}" alt="${nameOf(qbProduct)}, image ${i + 1} of ${imgs.length}" decoding="async">
    </figure>`).join('');
  play([...gal.children], { opacity: [0, 1] }, { duration: MO.base, ease: MO.eStandard });
  $('[data-qb-view]').href = qbUrl(qbProduct, slug);
});

/* ============================================================
   BINDINGS
   ============================================================ */
async function pickColor(slug) {
  if (slug === swatch().slug) return;
  ui.color[gender()] = slug;
  $$('[data-sw]', swatchRow).forEach((b) => {
    const on = b.dataset.sw === slug;
    b.setAttribute('aria-checked', String(on));
    b.tabIndex = on ? 0 : -1;
  });
  swapText(colorLabel, swatch().name);
  announce(`Uniform color: ${swatch().name}.`);
  await paint({ wave: true });
}

function bind() {
  swatchRow.addEventListener('click', (e) => {
    const b = e.target.closest('[data-sw]');
    if (b) pickColor(b.dataset.sw);
  });
  swatchRow.addEventListener('keydown', (e) => {
    const list = swatches();
    const i = list.findIndex((s) => s.slug === swatch().slug);
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!(e.key in keys)) return;
    e.preventDefault();
    const n = list[(i + keys[e.key] + list.length) % list.length];
    pickColor(n.slug);
    $(`[data-sw="${n.slug}"]`, swatchRow).focus();
  });
  const open = (e) => {
    const t = e.target.closest('[data-qb-open]');
    if (!t) return;
    const card = t.closest('[data-card]');
    const p = card ? productOf(card) : null;
    if (p) openQuickBuy(p, card ? $('.ncl-card__hit', card) : t, card?.dataset.color);
  };
  table.addEventListener('click', open);
  ledger.addEventListener('click', open);
}

export function initDyu() {
  render();
  bind();
  onGender((g, source) => {
    if (source !== root) { render(); return; }
    const parts = [swatchRow, colorLabel, table, ledger];
    play(parts, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished.then(() => {
      render();
      play(parts, { opacity: [0, 1] }, { duration: MO.instant });
      tween([...$$('[data-slot]', table), ...$$('[data-slot]', ledger)], { opacity: [0, 1], y: [MO.riseXs, 0] },
        { duration: MO.slow, ease: MO.eEntrance, delay: (i) => Math.min(i, 30) * 0.02 });
    });
  });
}
